import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Building2, LogOut, ArrowLeft, Search, Check, X, AlertTriangle,
  History, StickyNote, Clock, Ban, RotateCcw, ShieldCheck, Loader2,
} from 'lucide-react';

import { BACKEND_URL } from '../config/api';

const STATUS_TABS = [
  { key: 'pending', label: 'Pending', tone: 'amber', testId: 'tab-pending' },
  { key: 'approved', label: 'Approved', tone: 'emerald', testId: 'tab-approved' },
  { key: 'rejected', label: 'Rejected', tone: 'rose', testId: 'tab-rejected' },
  { key: 'suspended', label: 'Suspended', tone: 'orange', testId: 'tab-suspended' },
  { key: 'incomplete_pending_review', label: 'Incomplete', tone: 'slate', testId: 'tab-incomplete' },
];

const toneClass = {
  amber: 'bg-amber-50 text-amber-700 border-amber-200',
  emerald: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  rose: 'bg-rose-50 text-rose-700 border-rose-200',
  orange: 'bg-orange-50 text-orange-700 border-orange-200',
  slate: 'bg-slate-100 text-slate-700 border-slate-200',
};

export default function AdminEmployerQueue() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [status, setStatus] = useState('pending');
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const pageSize = 25;
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [counts, setCounts] = useState({});
  const [modal, setModal] = useState(null);
  // modal: { type: 'approve'|'reject'|'suspend'|'restore'|'history'|'notes', employer }
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/auth/me`, { withCredentials: true })
      .then((r) => { if (r.data.user_type !== 'admin') navigate('/login'); else setUser(r.data); })
      .catch(() => navigate('/login'));
  }, [navigate]);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ status, page: String(page), page_size: String(pageSize) });
      if (q) params.append('q', q);
      const res = await axios.get(`${BACKEND_URL}/api/admin/employers?${params.toString()}`, { withCredentials: true });
      setRows(res.data.employers || []);
      setTotal(res.data.total || 0);
      setCounts(res.data.counts || {});
    } catch {
      toast.error('Failed to load employer queue');
    } finally { setLoading(false); }
  };

  useEffect(() => { if (user) load(); }, [user, status, page]);

  const submitDecision = async (path, body, successMsg) => {
    setBusy(true);
    try {
      await axios.post(`${BACKEND_URL}/api/admin/employers/${modal.employer.user_id}/${path}`, body, { withCredentials: true });
      toast.success(successMsg);
      setModal(null);
      load();
    } catch (e) {
      const detail = e.response?.data?.detail;
      let msg = 'Action failed';
      if (typeof detail === 'string') msg = detail;
      else if (Array.isArray(detail) && detail[0]?.msg) msg = detail[0].msg;
      else if (detail && typeof detail === 'object' && detail.message) msg = detail.message;
      toast.error(msg);
    } finally { setBusy(false); }
  };

  const totalPages = useMemo(() => Math.max(1, Math.ceil(total / pageSize)), [total]);

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-employer-queue-page">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/admin/dashboard')} className="text-slate-600 hover:text-slate-900" data-testid="back-btn">
              <ArrowLeft className="h-5 w-5" />
            </button>
            <div className="flex items-center gap-2">
              <Building2 className="h-6 w-6 text-orange-500" />
              <h1 className="text-xl font-bold text-slate-900">Employer Approvals</h1>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-slate-600">{user?.name}</span>
            <button
              onClick={async () => { try { await axios.post(`${BACKEND_URL}/api/auth/logout`, {}, { withCredentials: true }); } finally { navigate('/login'); } }}
              className="btn-secondary text-sm flex items-center gap-2"
              data-testid="logout-btn"
            >
              <LogOut className="h-4 w-4" /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Tabs + counts */}
        <div className="flex flex-wrap gap-2 mb-4" role="tablist" aria-label="Employer approval status">
          {STATUS_TABS.map((t) => {
            const active = status === t.key;
            const count = counts[t.key] ?? 0;
            return (
              <button
                key={t.key}
                role="tab"
                aria-selected={active}
                onClick={() => { setStatus(t.key); setPage(1); }}
                className={`px-4 py-2 rounded-lg border text-sm font-medium transition-colors ${active
                  ? toneClass[t.tone]
                  : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'}`}
                data-testid={t.testId}
              >
                {t.label} <span className="ml-1 opacity-70">({count})</span>
              </button>
            );
          })}
        </div>

        {/* Search */}
        <div className="card mb-4">
          <form
            onSubmit={(e) => { e.preventDefault(); setPage(1); load(); }}
            className="flex items-center gap-2"
          >
            <Search className="h-4 w-4 text-slate-400" />
            <input
              type="text"
              placeholder="Search by name, email or business name"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="flex-1"
              data-testid="employer-search-input"
            />
            <button type="submit" className="btn-secondary text-sm" data-testid="employer-search-submit">Search</button>
          </form>
        </div>

        {/* List */}
        <div className="card" data-testid="employer-queue-list">
          {loading ? (
            <div className="py-12 flex items-center justify-center text-slate-500">
              <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading…
            </div>
          ) : rows.length === 0 ? (
            <div className="py-12 text-center">
              <Check className="h-12 w-12 text-emerald-500 mx-auto mb-3" />
              <p className="text-slate-600">Nothing here right now.</p>
              <p className="text-slate-500 text-sm">No employers in <span className="font-medium">{status}</span>.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {rows.map((emp) => (
                <EmployerRow
                  key={emp.user_id}
                  emp={emp}
                  onApprove={() => setModal({ type: 'approve', employer: emp })}
                  onReject={() => setModal({ type: 'reject', employer: emp })}
                  onSuspend={() => setModal({ type: 'suspend', employer: emp })}
                  onRestore={() => setModal({ type: 'restore', employer: emp })}
                  onHistory={() => setModal({ type: 'history', employer: emp })}
                  onNotes={() => setModal({ type: 'notes', employer: emp })}
                />
              ))}
            </div>
          )}

          {total > pageSize && (
            <div className="flex items-center justify-between mt-6 pt-4 border-t border-slate-200">
              <p className="text-sm text-slate-600">Page {page} of {totalPages} ({total} total)</p>
              <div className="flex gap-2">
                <button onClick={() => setPage(Math.max(1, page - 1))} disabled={page === 1} className="btn-secondary text-sm disabled:opacity-50" data-testid="page-prev">Previous</button>
                <button onClick={() => setPage(Math.min(totalPages, page + 1))} disabled={page >= totalPages} className="btn-secondary text-sm disabled:opacity-50" data-testid="page-next">Next</button>
              </div>
            </div>
          )}
        </div>
      </main>

      {modal?.type === 'approve' && (
        <ApproveModal
          employer={modal.employer}
          busy={busy}
          onClose={() => setModal(null)}
          onSubmit={(note) => submitDecision('approve', { note }, `Approved ${modal.employer.email}`)}
        />
      )}
      {(modal?.type === 'reject' || modal?.type === 'suspend') && (
        <ReasonModal
          kind={modal.type}
          employer={modal.employer}
          busy={busy}
          onClose={() => setModal(null)}
          onSubmit={(payload) => submitDecision(modal.type, payload, modal.type === 'reject' ? `Rejected ${modal.employer.email}` : `Suspended ${modal.employer.email}`)}
        />
      )}
      {modal?.type === 'restore' && (
        <RestoreModal
          employer={modal.employer}
          busy={busy}
          onClose={() => setModal(null)}
          onSubmit={(reason) => submitDecision('restore', { internal_reason: reason }, `Restored ${modal.employer.email}`)}
        />
      )}
      {modal?.type === 'history' && (
        <HistoryDrawer employer={modal.employer} onClose={() => setModal(null)} />
      )}
      {modal?.type === 'notes' && (
        <NotesDrawer employer={modal.employer} onClose={() => setModal(null)} />
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────
// Row
// ────────────────────────────────────────────────────────────
function EmployerRow({ emp, onApprove, onReject, onSuspend, onRestore, onHistory, onNotes }) {
  const status = emp.approval_status || 'incomplete_pending_review';
  const isPending = status === 'pending';
  const isApproved = status === 'approved';
  const isRejected = status === 'rejected';
  const isSuspended = status === 'suspended';
  return (
    <div
      className="border border-slate-200 rounded-lg p-4 hover:border-orange-300 transition-colors"
      data-testid={`employer-row-${emp.user_id}`}
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h4 className="font-semibold text-slate-900 truncate" data-testid={`employer-name-${emp.user_id}`}>{emp.name || emp.email}</h4>
            <StatusPill status={status} />
            {emp.business_name && (
              <span className="text-xs text-slate-500 truncate max-w-xs">· {emp.business_name}</span>
            )}
          </div>
          <p className="text-sm text-slate-500 mt-1 truncate" data-testid={`employer-email-${emp.user_id}`}>{emp.email}</p>
          <p className="text-xs text-slate-400 mt-1">
            Signed up {emp.created_at ? new Date(emp.created_at).toLocaleDateString() : 'n/a'} · Postcode {emp.postcode || '—'}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button onClick={onHistory} className="btn-secondary text-sm flex items-center gap-1" data-testid={`history-btn-${emp.user_id}`}>
            <History className="h-4 w-4" /> History
          </button>
          <button onClick={onNotes} className="btn-secondary text-sm flex items-center gap-1" data-testid={`notes-btn-${emp.user_id}`}>
            <StickyNote className="h-4 w-4" /> Notes
          </button>
          {(isPending) && (
            <button onClick={onApprove} className="btn-primary text-sm flex items-center gap-1" data-testid={`approve-btn-${emp.user_id}`}>
              <Check className="h-4 w-4" /> Approve
            </button>
          )}
          {(isPending || isApproved) && (
            <button onClick={onReject} className="bg-rose-500 hover:bg-rose-600 text-white px-3 py-2 rounded text-sm flex items-center gap-1" data-testid={`reject-btn-${emp.user_id}`}>
              <X className="h-4 w-4" /> Reject
            </button>
          )}
          {(isApproved || isPending) && (
            <button onClick={onSuspend} className="bg-amber-500 hover:bg-amber-600 text-white px-3 py-2 rounded text-sm flex items-center gap-1" data-testid={`suspend-btn-${emp.user_id}`}>
              <Ban className="h-4 w-4" /> Suspend
            </button>
          )}
          {(isRejected || isSuspended) && (
            <button onClick={onRestore} className="bg-emerald-500 hover:bg-emerald-600 text-white px-3 py-2 rounded text-sm flex items-center gap-1" data-testid={`restore-btn-${emp.user_id}`}>
              <RotateCcw className="h-4 w-4" /> Restore
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    pending: { label: 'Pending', tone: 'amber', Icon: Clock },
    approved: { label: 'Approved', tone: 'emerald', Icon: ShieldCheck },
    rejected: { label: 'Rejected', tone: 'rose', Icon: X },
    suspended: { label: 'Suspended', tone: 'orange', Icon: AlertTriangle },
    incomplete_pending_review: { label: 'Incomplete', tone: 'slate', Icon: Clock },
  };
  const s = map[status] || map.incomplete_pending_review;
  const tones = {
    amber: 'bg-amber-100 text-amber-700',
    emerald: 'bg-emerald-100 text-emerald-700',
    rose: 'bg-rose-100 text-rose-700',
    orange: 'bg-orange-100 text-orange-700',
    slate: 'bg-slate-100 text-slate-700',
  };
  const Icon = s.Icon;
  return (
    <span className={`text-xs px-2 py-0.5 rounded-full font-medium flex items-center gap-1 ${tones[s.tone]}`} data-testid={`status-pill-${status}`}>
      <Icon className="h-3 w-3" /> {s.label}
    </span>
  );
}

// ────────────────────────────────────────────────────────────
// Modals
// ────────────────────────────────────────────────────────────
function ModalShell({ title, children, onClose }) {
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 flex items-center justify-center p-4" role="dialog" aria-modal="true">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[85vh] overflow-y-auto">
        <div className="px-5 py-4 border-b border-slate-100 flex justify-between items-center sticky top-0 bg-white">
          <h2 className="text-lg font-bold text-slate-900" data-testid="modal-title">{title}</h2>
          <button className="text-slate-500 hover:text-slate-900" onClick={onClose} data-testid="modal-close-btn"><X className="h-5 w-5" /></button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}

function ApproveModal({ employer, busy, onClose, onSubmit }) {
  const [note, setNote] = useState('');
  return (
    <ModalShell title={`Approve ${employer.name || employer.email}?`} onClose={onClose}>
      <p className="text-sm text-slate-600 mb-3">This opens hiring access immediately. Employers can start chats, send offers, and unlock trades.</p>
      <label className="text-sm font-medium text-slate-700">Optional internal note</label>
      <textarea
        className="w-full mt-1"
        rows={3}
        value={note}
        onChange={(e) => setNote(e.target.value)}
        placeholder="Anything to record for the audit trail?"
        data-testid="approve-note-input"
      />
      <div className="flex justify-end gap-2 mt-4">
        <button className="btn-secondary" onClick={onClose} data-testid="approve-cancel-btn">Cancel</button>
        <button
          className="btn-primary flex items-center gap-2 disabled:opacity-60"
          onClick={() => onSubmit(note || null)}
          disabled={busy}
          data-testid="approve-submit-btn"
        >
          {busy && <Loader2 className="h-4 w-4 animate-spin" />} Approve
        </button>
      </div>
    </ModalShell>
  );
}

function ReasonModal({ kind, employer, busy, onClose, onSubmit }) {
  const isReject = kind === 'reject';
  const [internal, setInternal] = useState('');
  const [showPublic, setShowPublic] = useState(false);
  const [publicReason, setPublicReason] = useState('');
  return (
    <ModalShell
      title={`${isReject ? 'Reject' : 'Suspend'} ${employer.name || employer.email}`}
      onClose={onClose}
    >
      <label className="text-sm font-medium text-slate-700">Internal reason (admin-only) *</label>
      <textarea
        className="w-full mt-1"
        rows={3}
        value={internal}
        onChange={(e) => setInternal(e.target.value)}
        placeholder="Why is this happening? (Not shown to the employer)"
        data-testid="reason-internal-input"
      />

      <label className="flex items-center gap-2 mt-4 text-sm text-slate-700 cursor-pointer" data-testid="reason-show-public-toggle-wrap">
        <input
          type="checkbox"
          className="rounded"
          checked={showPublic}
          onChange={(e) => setShowPublic(e.target.checked)}
          data-testid="reason-show-public-toggle"
        />
        Show a sanitized reason to the employer on their {isReject ? 'rejected' : 'suspended'} screen
      </label>

      {showPublic && (
        <>
          <label className="text-sm font-medium text-slate-700 mt-3 block">Sanitized public reason</label>
          <textarea
            className="w-full mt-1"
            rows={2}
            maxLength={280}
            value={publicReason}
            onChange={(e) => setPublicReason(e.target.value)}
            placeholder="Short, safe explanation (max 280 chars). Do NOT paste internal notes here."
            data-testid="reason-public-input"
          />
          <p className="text-xs text-slate-500">{publicReason.length}/280</p>
        </>
      )}

      <div className="flex justify-end gap-2 mt-5">
        <button className="btn-secondary" onClick={onClose} data-testid="reason-cancel-btn">Cancel</button>
        <button
          className={`${isReject ? 'bg-rose-500 hover:bg-rose-600' : 'bg-amber-500 hover:bg-amber-600'} text-white px-4 py-2 rounded text-sm flex items-center gap-2 disabled:opacity-60`}
          disabled={busy || internal.trim().length < 3}
          onClick={() => onSubmit({
            internal_reason: internal.trim(),
            public_reason: showPublic && publicReason.trim() ? publicReason.trim() : null,
            show_public_reason: showPublic,
          })}
          data-testid="reason-submit-btn"
        >
          {busy && <Loader2 className="h-4 w-4 animate-spin" />} {isReject ? 'Reject' : 'Suspend'}
        </button>
      </div>
    </ModalShell>
  );
}

function RestoreModal({ employer, busy, onClose, onSubmit }) {
  const [reason, setReason] = useState('');
  const previous = employer.approval_status;
  return (
    <ModalShell title={`Restore ${employer.name || employer.email}`} onClose={onClose}>
      <p className="text-sm text-slate-600 mb-3">
        {previous === 'rejected'
          ? 'This employer will move back to pending — hiring access stays blocked until you approve them.'
          : 'This employer will move back to their most recent non-suspended status (or pending if none). Hiring access is unlocked only if they were previously approved.'}
      </p>
      <label className="text-sm font-medium text-slate-700">Internal reason *</label>
      <textarea
        className="w-full mt-1"
        rows={3}
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        placeholder="Why are you restoring this employer?"
        data-testid="restore-reason-input"
      />
      <div className="flex justify-end gap-2 mt-4">
        <button className="btn-secondary" onClick={onClose} data-testid="restore-cancel-btn">Cancel</button>
        <button
          className="bg-emerald-500 hover:bg-emerald-600 text-white px-4 py-2 rounded text-sm flex items-center gap-2 disabled:opacity-60"
          disabled={busy || reason.trim().length < 3}
          onClick={() => onSubmit(reason.trim())}
          data-testid="restore-submit-btn"
        >
          {busy && <Loader2 className="h-4 w-4 animate-spin" />} Restore
        </button>
      </div>
    </ModalShell>
  );
}

function HistoryDrawer({ employer, onClose }) {
  const [items, setItems] = useState(null);
  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/admin/employers/${employer.user_id}/history`, { withCredentials: true })
      .then((r) => setItems(r.data.history || []))
      .catch(() => setItems([]));
  }, [employer.user_id]);
  return (
    <ModalShell title={`History — ${employer.name || employer.email}`} onClose={onClose}>
      {items === null ? (
        <div className="py-6 text-center text-slate-500"><Loader2 className="inline h-5 w-5 animate-spin" /></div>
      ) : items.length === 0 ? (
        <p className="py-6 text-center text-slate-500">No transitions recorded yet.</p>
      ) : (
        <ul className="space-y-3" data-testid="history-list">
          {items.map((it, idx) => (
            <li key={idx} className="border border-slate-200 rounded-lg p-3">
              <div className="flex justify-between items-center gap-2 flex-wrap">
                <p className="text-sm">
                  <span className="text-slate-500">{it.details?.previous_status || '—'}</span>
                  {' → '}
                  <span className="font-semibold text-slate-900">{it.details?.new_status}</span>
                </p>
                <span className="text-xs text-slate-500">{new Date(it.timestamp).toLocaleString()}</span>
              </div>
              {it.details?.internal_reason && (
                <p className="text-xs text-slate-600 mt-1"><span className="font-medium">Internal:</span> {it.details.internal_reason}</p>
              )}
              {it.details?.public_reason && (
                <p className="text-xs text-slate-600 mt-0.5"><span className="font-medium">Public:</span> {it.details.public_reason}</p>
              )}
            </li>
          ))}
        </ul>
      )}
    </ModalShell>
  );
}

function NotesDrawer({ employer, onClose }) {
  const [items, setItems] = useState(null);
  const [content, setContent] = useState('');
  const [saving, setSaving] = useState(false);

  const load = () => {
    axios.get(`${BACKEND_URL}/api/admin/employers/${employer.user_id}/notes`, { withCredentials: true })
      .then((r) => setItems(r.data.notes || []))
      .catch(() => setItems([]));
  };
  useEffect(load, [employer.user_id]);

  const save = async () => {
    if (!content.trim()) return;
    setSaving(true);
    try {
      await axios.post(`${BACKEND_URL}/api/admin/employers/${employer.user_id}/notes`, { content: content.trim() }, { withCredentials: true });
      setContent('');
      toast.success('Note saved');
      load();
    } catch { toast.error('Failed to save note'); }
    finally { setSaving(false); }
  };

  return (
    <ModalShell title={`Internal notes — ${employer.name || employer.email}`} onClose={onClose}>
      <textarea
        className="w-full"
        rows={3}
        value={content}
        onChange={(e) => setContent(e.target.value)}
        placeholder="Admin-only internal note (never shown to the employer)"
        data-testid="note-input"
      />
      <div className="flex justify-end mt-2">
        <button onClick={save} disabled={saving || !content.trim()} className="btn-primary text-sm flex items-center gap-2 disabled:opacity-60" data-testid="note-save-btn">
          {saving && <Loader2 className="h-4 w-4 animate-spin" />} Save note
        </button>
      </div>

      <div className="mt-4 border-t pt-4">
        {items === null ? (
          <div className="py-6 text-center text-slate-500"><Loader2 className="inline h-5 w-5 animate-spin" /></div>
        ) : items.length === 0 ? (
          <p className="py-6 text-center text-slate-500">No notes yet.</p>
        ) : (
          <ul className="space-y-3" data-testid="notes-list">
            {items.map((it) => (
              <li key={it.note_id} className="border border-slate-200 rounded-lg p-3">
                <p className="text-sm text-slate-900">{it.content}</p>
                <p className="text-xs text-slate-500 mt-1">
                  {it.admin_name} · {new Date(it.created_at).toLocaleString()}
                </p>
              </li>
            ))}
          </ul>
        )}
      </div>
    </ModalShell>
  );
}
